/**
 * Confirm Dialog Component
 * 
 * Reusable confirmation dialog for dangerous actions like deletions.
 * 
 * Features:
 * - Customizable title, message, and button text
 * - Loading state support
 * - Accessible design
 * - Mobile-responsive
 * 
 * @author AI Assistant
 * @created 2024
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    minWidth: 320,
    maxWidth: 500,
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
      maxWidth: '95vw',
      margin: theme.spacing(1)
    }
  }
}));

const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmColor = 'primary',
  loading = false,
  disabled = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleConfirm = () => {
    if (!loading && !disabled) {
      onConfirm();
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="confirm-dialog-title">
        {title}
      </DialogTitle>
      
      <DialogContent>
        <DialogContentText id="confirm-dialog-description">
          {message}
        </DialogContentText>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          color="inherit"
          size={isMobile ? 'medium' : 'small'}
        >
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading || disabled}
          color={confirmColor}
          variant="contained"
          size={isMobile ? 'medium' : 'small'}
          startIcon={loading && <CircularProgress size={16} />}
        >
          {loading ? 'Processing...' : confirmText}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default ConfirmDialog; 